import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import PageVisibilityGate from './components/PageVisibilityGate';
import RequireAdmin from './components/RequireAdmin';
import { AuthProvider } from './lib/auth';
import ScrollToTop from './components/signal/ScrollToTop';
import { AppBoundary } from './shell/AppBoundary';

// Every page loads on demand, so a visitor to the island never downloads the admin.
const PodcastBlogIndex = lazy(() => import('./components/PodcastBlogIndex'));
const PodcastBlogPost = lazy(() => import('./components/PodcastBlogPost'));
const BlogPost = lazy(() => import('./components/BlogPost'));
const PrivacyPage = lazy(() => import('./components/PrivacyPage'));
const TermsOfServicePage = lazy(() => import('./components/TermsOfServicePage'));
const LoginPage = lazy(() => import('./components/LoginPage'));
const PostIndex = lazy(() => import('./components/PostIndex'));
const AuditList = lazy(() => import('./components/AuditList').then((m) => ({ default: m.AuditList })));
const AdminWebhookPage = lazy(() => import('./components/AdminWebhookPage'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const DashboardPage = lazy(() => import('./components/DashboardPage'));
const ContentEditorPage = lazy(() => import('./components/admin/ContentEditorPage'));
const VisibilityPage = lazy(() => import('./components/admin/VisibilityPage'));

// The world is its own chunk: blog, report and admin pages never load it or its images.
const WorldLayout = lazy(() => import('./world/WorldLayout'));
const WorldPanels = () => import('./world/routes/panels');
const ServicesPanel = lazy(() => WorldPanels().then((m) => ({ default: m.ServicesRoute })));
const WorkPanel = lazy(() => WorldPanels().then((m) => ({ default: m.WorkRoute })));
const ClientPanel = lazy(() => WorldPanels().then((m) => ({ default: m.ClientRoute })));
const ProjectsPanel = lazy(() => WorldPanels().then((m) => ({ default: m.ProjectsPanel })));
const PersonPanel = lazy(() => WorldPanels().then((m) => ({ default: m.PersonPanel })));
const PeoplePanel = lazy(() => WorldPanels().then((m) => ({ default: m.PeoplePanel })));
const AboutPanel = lazy(() => WorldPanels().then((m) => ({ default: m.AboutRoute })));
const MediaPanel = lazy(() => WorldPanels().then((m) => ({ default: m.MediaRoute })));
const ContactPanel = lazy(() => WorldPanels().then((m) => ({ default: m.ContactRoute })));
const AiReadinessPanel = lazy(() => WorldPanels().then((m) => ({ default: m.AiReadinessRoute })));

// The audit front-end is being rebuilt. Set this flag to drive the existing backend end to end.
const LEGACY_AUDIT = import.meta.env.VITE_ENABLE_LEGACY_AUDIT === 'true';
const AiReadinessPage = lazy(() => import('./components/AiReadinessPage'));
const AuditJourneyPage = lazy(() => import('./components/AuditJourneyPage'));

function App() {
  return (
    <Router>
      <AuthProvider>
        <ScrollToTop />
        <AppBoundary>
        <Suspense fallback={null}>
        <Routes>
          {/* ── The world: one persistent scene, each destination a panel on top ── */}
          <Route element={<Suspense fallback={null}><WorldLayout /></Suspense>}>
            <Route index element={null} />
            <Route path="services/:topic?" element={<ServicesPanel />} />
            <Route path="work" element={<WorkPanel />} />
            <Route path="work/:client" element={<ClientPanel />} />
            <Route path="products/*" element={null} />
            <Route path="projects/:product?" element={<ProjectsPanel />} />
            <Route path="people" element={<PeoplePanel />} />
            <Route path="people/:person" element={<PersonPanel />} />
            <Route path="about" element={<AboutPanel />} />
            <Route path="media/:post?" element={<MediaPanel />} />
            <Route path="contact" element={<ContactPanel />} />
            <Route path="ai-readiness" element={<AiReadinessPanel />} />
          </Route>

          {/* ── Full pages ─────────────────────────────────────────── */}
          <Route path="/blog/insights" element={<PageVisibilityGate pageKey="blogIndex"><PodcastBlogIndex /></PageVisibilityGate>} />
          <Route path="/blog/insights/:slug" element={<PageVisibilityGate pageKey="blogIndex"><PodcastBlogPost /></PageVisibilityGate>} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />

          {LEGACY_AUDIT && (
            <>
              <Route path="/legacy/ai-readiness" element={<AiReadinessPage />} />
              <Route path="/ai-readiness/audit" element={<AuditJourneyPage />} />
            </>
          )}

          {/* ── Public shareable audit report: emailed links must keep working ── */}
          <Route path="/post/audit/:slug" element={<PageVisibilityGate pageKey="postIndex"><BlogPost /></PageVisibilityGate>} />

          {/* ── Login ────────────────────────────────────────────── */}
          <Route path="/login" element={<LoginPage />} />

          {/* ── Admin-only routes (guarded by RequireAdmin) ───────── */}
          <Route path="/post" element={<RequireAdmin><PageVisibilityGate pageKey="postIndex"><PostIndex /></PageVisibilityGate></RequireAdmin>} />
          <Route path="/audits" element={<RequireAdmin><AuditList /></RequireAdmin>} />
          <Route path="/admin/webhook" element={<RequireAdmin><AdminWebhookPage /></RequireAdmin>} />
          <Route path="/admin/dashboard" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
          <Route path="/dashboard" element={<RequireAdmin><DashboardPage /></RequireAdmin>} />
          <Route path="/dashboard/visibility" element={<RequireAdmin><VisibilityPage /></RequireAdmin>} />
          <Route path="/dashboard/content/:section?/:tab?" element={<RequireAdmin><ContentEditorPage /></RequireAdmin>} />

          {/* Catch-all: redirect removed/unknown routes home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        </AppBoundary>
      </AuthProvider>
    </Router>
  );
}

export default App;
